/**
 * Human-readable field labels for validation error messages
 * Maps technical field names to user-friendly Polish labels
 */
export const FIELD_LABELS: Record<string, string> = {
  // Invoice basics
  type: 'Typ faktury',
  scheme: 'Schemat rozliczeń',
  invoiceNumber: 'Numer faktury',
  issueDate: 'Data wystawienia',
  saleDate: 'Data sprzedaży',
  reservationId: 'Rezerwacja',
  companyId: 'Firma',
  currency: 'Waluta',
  status: 'Status',
  paymentMethod: 'Forma płatności',
  paymentComment: 'Komentarz do płatności',
  paidAmount: 'Kwota zapłacona',
  paidAt: 'Data zapłaty',

  // Seller
  sellerName: 'Nazwa sprzedawcy',
  sellerNip: 'NIP sprzedawcy',
  sellerStreetNo: 'Ulica i nr sprzedawcy',
  sellerZipCode: 'Kod pocztowy sprzedawcy',
  sellerCity: 'Miasto sprzedawcy',
  sellerCountry: 'Kraj sprzedawcy',

  // Buyer/Contractor
  buyerName: 'Nazwa nabywcy',
  buyerNip: 'NIP nabywcy',
  buyerCountryIso: 'Kod kraju nabywcy',
  isBuyerVatEu: 'Status VAT-UE nabywcy',
  buyerIsJst: 'Nabywca jest JST',
  buyerStreetNo: 'Ulica i nr nabywcy',
  buyerZipCode: 'Kod pocztowy nabywcy',
  buyerCity: 'Miasto nabywcy',
  buyerCountry: 'Kraj nabywcy',

  // Recipient (optional)
  useDifferentRecipient: 'Odbiorca inny niż nabywca',
  recipientName: 'Nazwa odbiorcy',
  recipientStreetNo: 'Ulica i nr odbiorcy',
  recipientZipCode: 'Kod pocztowy odbiorcy',
  recipientCity: 'Miasto odbiorcy',
  recipientCountry: 'Kraj odbiorcy',

  // Items
  items: 'Pozycje faktury',
  'items[0]': 'Pozycja 1',
  'items[1]': 'Pozycja 2',
  'items[2]': 'Pozycja 3',
  'items.name': 'Nazwa pozycji',
  'items.description': 'Opis pozycji',
  'items.quantity': 'Ilość',
  'items.unitNet': 'Cena netto',
  'items.unitGross': 'Cena brutto',
  'items.vatType': 'Typ VAT',
  'items.vatExemptNote': 'Podstawa zwolnienia z VAT',

  // PFRON Refund
  pfronCertificateNumber: 'Numer zaświadczenia PFRON',
  pfronReasonForExemption: 'Powód zwolnienia PFRON',

  // Prepayment payment
  prepaymentInvoiceId: 'Faktura zaliczkowa',

  // Correction
  correctionInvoiceId: 'Faktura korygowana',
  correctionReason: 'Powód korekty',

  // Prepayments selection
  selectedPrepaymentIds: 'Wybrane zaliczki',

  // Template
  templateVariant: 'Wariant szablonu PDF',
};

/**
 * Get human-readable label for a field name
 * @param fieldName - Technical field name (e.g., 'sellerNip', 'items[0].name')
 * @returns User-friendly Polish label
 */
export const getFieldLabel = (fieldName: string): string => {
  // Direct match
  if (FIELD_LABELS[fieldName]) {
    return FIELD_LABELS[fieldName];
  }

  // Handle array notation: items[0].name → "Nazwa pozycji (pozycja 1)"
  const arrayMatch = fieldName.match(/^items\[(\d+)\]\.(.+)$/);
  if (arrayMatch) {
    const index = parseInt(arrayMatch[1], 10);
    const subField = arrayMatch[2];
    const subFieldLabel = FIELD_LABELS[`items.${subField}`] || subField;
    return `${subFieldLabel} (pozycja ${index + 1})`;
  }

  // Fallback: return original field name
  return fieldName;
};
